
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Flex, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { DollarOutlined, ExportOutlined, PicLeftOutlined, UserOutlined } from "@ant-design/icons";

import SText from "@/styles/SText";
import IconFolder from "@/assets/images/fileIcon/folder.svg";
import Dict from "@/components/Dict";
import { DictEnum } from "@/types/dict";
import { CreditLevelSelect, CurrencySelect, CountrySelect, DepartmentSelect } from "@/components/Selector";
import { Supplier } from "@/types/purchase";
import { CustomerTabType } from "..";
import { PageId } from "@/types/page";
import { CommonStateEnum } from "@/types/common";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName } from "@/utils/user";

type UseColumn = (props: {
  onDetail: (params: Supplier | null) => void;
  onDel: (id: string) => void;
  onEnable: (id: string) => void;
  onOpenTab: (type: CustomerTabType, item: Supplier) => void;
  onExportPrice: (supplier: Supplier) => void;
  state?: CommonStateEnum;
}) => ColumnsType<Supplier>

const useSupplierColumn: UseColumn = ({ onDetail, onDel, onEnable, onOpenTab, onExportPrice, state }) => {
  const { t } = useTranslation();

  const columns: ColumnsType<Supplier> = useMemo(() => [
    {
      title: <SText text={t("purchase.code")} />,
      dataIndex: "code",
      width: 100,
      fixed: "left",
      sorter: (a, b) => {
        const valA = a.code ?? "";
        const valB = b.code ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, item) => <SText text={item.code} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("purchase.principal")} />,
      dataIndex: "principal",
      width: 100,
      render: (val, item) => (
        <SText text={item.principal ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.name")} />,
      dataIndex: "name",
      width: 140,
      render: (_, item) => (
        <SText text={item.name ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.shortName")} />,
      dataIndex: "shortName",
      width: 80,
      render: (_, item) => (
        <SText text={item.shortName ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.country")} />,
      dataIndex: "country",
      width: 100,
      render: (val, item) => (
        <CountrySelect key={item.id} value={val || ""} readOnly={true} />
      ),
    },
    {
      title: <SText text={t("purchase.address")} />,
      dataIndex: "address",
      width: 200,
      render: (_, item) => (
        <SText text={item.address ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.zipCode")} />,
      dataIndex: "zipCode",
      width: 70,
      render: (_, item) => (
        <SText text={item.zipCode ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.email")} />,
      dataIndex: "email",
      width: 160,
      render: (_, item) => (
        <SText text={item.email ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.contact")} />,
      dataIndex: "contact",
      width: 80,
      render: (_, item) => (
        <SText text={item.contact ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.phone")} />,
      dataIndex: "phone",
      width: 100,
      render: (_, item) => (
        <SText text={item.phone ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.fax")} />,
      dataIndex: "fax",
      width: 100,
      render: (_, item) => (
        <SText text={item.fax ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.mobile")} />,
      dataIndex: "mobile",
      width: 100,
      render: (_, item) => (
        <SText text={item.mobile ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.account1")} />,
      dataIndex: "account1",
      width: 140,
      render: (_, item) => (
        <SText text={item.account1 ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.bank1")} />,
      dataIndex: "bank1",
      width: 140,
      render: (_, item) => (
        <SText text={item.bank1 ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.swiftCode1")} />,
      dataIndex: "swiftCode1",
      width: 80,
      render: (_, item) => (
        <SText text={item.swiftCode1 ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.account2")} />,
      dataIndex: "account2",
      width: 140,
      render: (_, item) => (
        <SText text={item.account2 ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.bank2")} />,
      dataIndex: "bank2",
      width: 140,
      render: (_, item) => (
        <SText text={item.bank2 ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.swiftCode2")} />,
      dataIndex: "swiftCode2",
      width: 80,
      render: (_, item) => (
        <SText text={item.swiftCode2 ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.currency")} />,
      dataIndex: "currency",
      width: 80,
      render: (val, item) => (
        <CurrencySelect key={item.id} value={val || ""} readOnly={true} />
      ),
    },
    {
      title: <SText text={t("purchase.creditLevel")} />,
      dataIndex: "creditLevel",
      width: 80,
      render: (val, item) => (
        <CreditLevelSelect value={val} readOnly key={item.id + "creditLevel"} />
      ),
    },
    {
      title: <SText text={t("purchase.creditLine")} />,
      dataIndex: "creditLine",
      width: 80,
      render: (_, item) => (
        <SText text={item.creditLine ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.depositRate")} />,
      dataIndex: "depositRate",
      width: 80,
      render: (_, item) => (
        <SText
          text={item.depositRate ?? ""}
          style={{ width: "100%" }}
          suffix="%"
        />
      ),
    },
    {
      title: <SText text={t("purchase.tradeTerm")} />,
      dataIndex: "tradeTerm",
      width: 100,
      render: (val, item) => (
        <Dict type={DictEnum.PURCHASE_TRADE_TERM} value={item.tradeTerm} readOnly />
      ),
    },
    {
      title: <SText text={t("purchase.payMethod")} />,
      dataIndex: "payMethod",
      width: 100,
      render: (val, item) => (
        <Dict type={DictEnum.PURCHASE_PAY_METHOD} value={item.payMethod} readOnly />
      ),
    },
    {
      title: <SText text={t("purchase.affiliates")} />,
      dataIndex: "affiliates",
      width: 140,
      render: (_, item) => (
        <SText text={item.affiliates ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.org")} />,
      dataIndex: "orgId",
      width: 140,
      render: (val, item) => (
        <DepartmentSelect value={val} readOnly style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.billingDays")} />,
      dataIndex: "billingDays",
      width: 80,
      render: (val, item) => (
        <SText text={item.billingDays ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.taxRate")} />,
      dataIndex: "taxRate",
      width: 100,
      render: (_, item) => (
        <SText text={item.taxRate ?? ""} style={{ width: "100%" }} suffix="%" />
      ),
    },
    {
      title: <SText text={t("purchase.thdCode")} />,
      dataIndex: "thdCode",
      width: 100,
      render: (_, item) => (
        <SText text={item.thdCode ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("common.creator")} />,
      dataIndex: "creator",
      width: 100,
      render: (_, item) => {
        let userData = item.creator;
        return (
          userData && <Flex>
            <div>
              <WMSAvatar creator={userData} uniChar={userData.id}
                avatarId={userData.avatar}
                size={24} alt={getUserAvatarTip(userData)}
              />
            </div>
            <SText text={getUserName(userData)} />
          </Flex>
        )
      },
    },
    {
      title: <SText text={t("purchase.accounts")} />,
      dataIndex: "accounts",
      fixed: "right",
      width: 60,
      hidden: state === CommonStateEnum.UN_ACTIVE,
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          {/* <img
            src={IconFolder}
            alt=""
            onClick={() => onOpenTab("doc", item)}
            style={{ cursor: "pointer" }}
          /> */}
          <UserOutlined style={{ cursor: "pointer" }} onClick={() => onOpenTab("user", item)} />
        </Flex>
      ),
    },
    {
      title: <SText text={t("purchase.delivery")} />,
      fixed: "right",
      width: 60,
      hidden: state === CommonStateEnum.UN_ACTIVE,
      render: (_, item) => (
        <Button
          type="link"
          icon={<PicLeftOutlined />}
          onClick={() => onOpenTab("delivery", item)}
        />
      ),
    },
    {
      title: <SText text={t("purchase.price")} />,
      dataIndex: "price",
      fixed: "right",
      width: 60,
      hidden: state === CommonStateEnum.UN_ACTIVE,
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <Button
            type="primary"
            icon={<DollarOutlined />}
            onClick={() => onOpenTab("price", item)}
          />
        </Flex>
      ),
    },
    {
      title: <SText text={t("purchase.relatedDocuments")} />,
      dataIndex: "relatedDocuments",
      fixed: "right",
      width: 60,
      hidden: state === CommonStateEnum.UN_ACTIVE,
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <img
            src={IconFolder}
            alt=""
            onClick={() => onOpenTab("doc", item)}
            style={{ cursor: "pointer" }}
          />
        </Flex>
      ),
    },
    {
      title: t("common.opt"),
      dataIndex: "operation",
      width: 120,
      fixed: "right",
      render: (val, record) => (
        <Flex justify="space-between">
          {
            state === CommonStateEnum.UN_ACTIVE ?
              <Button type="primary" onClick={() => onEnable(record.id)} >{t('org.enable')}</Button>
              : <>
                <Button type="primary" onClick={() => onDetail(record)}>
                  {t("common.detail")}
                </Button>
                <Button type="link" danger onClick={() => onDel(record.id)}>
                  {t("org.disable")}
                </Button>
              </>
          }
          <Tooltip title={t("purchase.hisPriceExport")}>
            <Button
              icon={<ExportOutlined />}
              onClick={() => onExportPrice(record)}
            />

          </Tooltip>
        </Flex>
      ),
    },
  ], [t, state]);

  return columns;
}

export default useSupplierColumn;